import { fetchJSON } from '@bonnet/core/fetch';
import _get from 'lodash/get';
import hasDom from 'has-dom';
import { Module } from '@bonnet/modules';
import { Cookies } from 'react-cookie';
import getConfig from '@bonnet/core/config';
import { objectDuckCreator, DuckSelector, createSelector } from '@atc/modular-redux';
import { join } from '@bonnet/core/pathname';
import processPersonalizationResponse from './processPersonalizationResponse';
import translateConsumerInsights from './translateConsumerInsights';
import translateDataLayerConsumerInsights from './translateDataLayerConsumerInsights';
import translateDataLayerConsumerAdTargetsWithoutCAMPBias from './translateDataLayerConsumerAdTargetsWithoutCAMPBias';
import translateDataLayerConsumerMarketingSegments from './translateDataLayerConsumerMarketingSegments';
import translateDataLayerConsumerAdTargets from './translateDataLayerConsumerAdTargets';
const LOCAL_STORAGE_KEY = 'pe_data';
function getPixallId(cookies) {
  return cookies.get('abc') || cookies.get('pxa_id') || 0;
}
const PersonalizationEngineModuleCreator = function () {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    getApiQuery,
    ssr = true,
    customBasePath
  } = options;
  const personalizationFetcher = _ref => {
    let {
      url,
      ctx,
      query
    } = _ref;
    // allow custom api query parameters to be passed
    if (getApiQuery && typeof getApiQuery === 'function') {
      Object.assign(query, getApiQuery(ctx));
    }

    // If server side implementation then add ipAddr from X-Forwarded-For if not added in getApiQuery
    // Client side we will add this param in the proxy since we won't have it here.
    if (!query.ipAddr && ctx.req) {
      const xFF = ctx.req.headers['X-Forwarded-For'] || '';
      const ip = xFF.split(',')[0];
      if (ip) {
        query.ipAddr = ip;
      }
    }
    return fetchJSON(url, {
      headers: {
        'x-api-key': process.env.personalizationEngineApiKey
      },
      query,
      circuitBreaker: {
        id: 'personalizationEngine',
        timeout: 3000,
        resetTimeout: 30000,
        fallback: {}
      }
    });
  };
  const personalizationDuck = objectDuckCreator({
    store: 'personalizationEngine',
    initialState: {}
  }).extend({
    creators: duck => ({
      fetchClientPersonalization: (zip, params) => async dispatch => {
        const cookies = new Cookies();
        const query = {
          pixallId: getPixallId(cookies),
          zip,
          ...params
        };
        const bonnetConfig = getConfig();
        const basePath = bonnetConfig.assetPrefix || customBasePath || process.env.BASE_PATH;
        const data = await personalizationFetcher({
          // Client side we want to go through the bonnet proxy which limits client side data and adds the api key
          // Server side go directly to the PE endpoint to avoid unnecessary http proxy requests.
          url: join(basePath, 'pep'),
          ctx: {
            // eslint-disable-next-line no-undef
            query: globalThis === null || globalThis === void 0 ? void 0 : globalThis.location.search
          },
          query
        });
        const peState = processPersonalizationResponse(data);
        if (hasDom()) {
          // eslint-disable-next-line no-undef
          window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(peState));
        }
      }
    }),
    selectors: duck => ({
      selectIsEmptyState: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => Object.keys(localState).length === 0)),
      // There are cases (especially in bonnet-next with this duck's non-ssr mode) in where there may be
      // Personalization Data added to localState
      // but not available to redux on initial page load as the client side ctx-middleware has not run again.
      // This selector will automatically look for data in local storage to set initial state but
      // only if it is a client-side selector and there is no existing state within redux for this duck
      getDuckOrLocalState: new DuckSelector(selectors => createSelector(selectors.getDuckState, selectors.selectIsEmptyState, (localState, isEmptyState) => {
        if (hasDom() && isEmptyState) {
          const localStorageState = window.localStorage.getItem(LOCAL_STORAGE_KEY);
          if (localStorageState) {
            return JSON.parse(localStorageState);
          }
        }
        return localState;
      })),
      // This selector gets duck state without checking local storage
      getDuckStateTranslated: new DuckSelector(selectors => createSelector(selectors.getDuckState, _ref2 => {
        let {
          personalization = {},
          consumerMarketingSegments = {},
          consumerAdTargets = {},
          consumerAdTargetsWithoutCAMPBias = {}
        } = _ref2;
        return translateConsumerInsights(personalization, consumerMarketingSegments, consumerAdTargets, consumerAdTargetsWithoutCAMPBias);
      })),
      selectPersonalizationRaw: new DuckSelector(selectors => createSelector(selectors.getDuckOrLocalState, localState => localState.personalization)),
      selectConsumerMarketingSegmentsRaw: new DuckSelector(selectors => createSelector(selectors.getDuckOrLocalState, localState => localState.consumerMarketingSegments)),
      selectConsumerAdTargetsRaw: new DuckSelector(selectors => createSelector(selectors.getDuckOrLocalState, localState => localState.consumerAdTargets)),
      selectConsumerAdTargetsWithoutCAMPBiasRaw: new DuckSelector(selectors => createSelector(selectors.getDuckOrLocalState, localState => localState.consumerAdTargetsWithoutCAMPBias)),
      selectExperiments: new DuckSelector(selectors => createSelector(selectors.getDuckOrLocalState, localState => localState.experiments)),
      selectPreferences: new DuckSelector(selectors => createSelector(selectors.getDuckOrLocalState, localState => localState.preferences)),
      selectConsumerInsights: new DuckSelector(selectors => createSelector(selectors.selectPersonalizationRaw, personalization => translateDataLayerConsumerInsights(personalization))),
      selectPersonalization: new DuckSelector(selectors => createSelector(selectors.selectPersonalizationRaw, selectors.selectConsumerMarketingSegmentsRaw, selectors.selectConsumerAdTargetsRaw, selectors.selectConsumerAdTargetsWithoutCAMPBiasRaw, function () {
        let personalization = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        let consumerMarketingSegments = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        let consumerAdTargets = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
        let consumerAdTargetsWithoutCAMPBias = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
        return translateConsumerInsights(personalization, consumerMarketingSegments, consumerAdTargets, consumerAdTargetsWithoutCAMPBias);
      })),
      selectConsumerAdTargets: new DuckSelector(selectors => createSelector(selectors.selectConsumerAdTargetsRaw, selectors.selectPreferences, function () {
        let consumerAdTargets = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        let preferences = arguments.length > 1 ? arguments[1] : undefined;
        return translateDataLayerConsumerAdTargets(consumerAdTargets, preferences);
      })),
      selectConsumerAdTargetsWithoutCAMPBias: new DuckSelector(selectors => createSelector(selectors.selectConsumerAdTargetsWithoutCAMPBiasRaw, function () {
        let consumerAdTargetsWithoutCAMPBias = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        return translateDataLayerConsumerAdTargetsWithoutCAMPBias(consumerAdTargetsWithoutCAMPBias);
      })),
      selectConsumerMarketingSegments: new DuckSelector(selectors => createSelector(selectors.selectConsumerMarketingSegmentsRaw, function () {
        let consumerMarketingSegments = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        return translateDataLayerConsumerMarketingSegments(consumerMarketingSegments);
      })),
      selectExperimentsFormatted: new DuckSelector(selectors => createSelector(selectors.selectExperiments, experiments => experiments ? Object.keys(experiments).map(k => `${k}:${experiments[k]}`).join(',') : ''))
    })
  });
  const ctxMiddleware = _ref3 => {
    let {
      duck
    } = _ref3;
    return ctx => {
      if (ssr) {
        const authConsumerId = ctx.cookies.get('auth_consumer_id');
        const query = {
          pixallId: getPixallId(ctx.cookies),
          consumerId: _get(ctx.cookies, ['cookies', 'auth_consumer_id']) || authConsumerId
        };

        // assign a fetch to be processed with the composite fetch
        ctx.fetchCompositeJSON.addFetch('adPersonalization', personalizationFetcher({
          // Client side we want to go through the bonnet proxy which limits client side data and adds the api key
          // Server side go directly to the PE endpoint to avoid unneccesary http proxy requests.
          url: ctx.req ? process.env.personalizationEngineURL : join(getConfig().assetPrefix, 'pep'),
          ctx,
          query
        }));
        ctx.batchDispatch.createActions(deferredCtx => {
          const response = _get(deferredCtx, 'data.adPersonalization');
          const peState = processPersonalizationResponse(response);
          return [duck.creators.assign(peState)];
        });
      }
    };
  };
  return new Module({
    duck: personalizationDuck,
    ctxMiddleware
  });
};
export default PersonalizationEngineModuleCreator;